import React, { useState } from "react";
import { Wrench, Lock, CheckCircle, ArrowRight, Clock, Zap, Bike, Maximize2, X, PlayCircle, Video } from "lucide-react";

// Pomocná komponenta pro responzivní zobrazení YouTube videa
const VideoEmbed = ({ embedId, title }) => (
  <div className="relative w-full overflow-hidden rounded-xl shadow-md border border-slate-200" style={{ paddingTop: '56.25%' }}>
    <iframe
      className="absolute top-0 left-0 w-full h-full"
      src={`https://www.youtube.com/embed/${embedId}`}
      title={title}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    ></iframe>
  </div>
);

const GuidesPage = ({ navigateTo }) => {
  const [isSchemaOpen, setIsSchemaOpen] = useState(false);
  
  // Stav pro přepínání videí u motorů
  const [activeMotorTab, setActiveMotorTab] = useState('750w');

  const batterySteps = [
    {
      id: 1,
      title: "Oddělení montážní lišty",
      description: "Před samotnou montáží odemkněte zámek baterie přiloženým klíčkem a vysuňte montážní lištu (držák) ze spodní části baterie. Tuto lištu budeme šroubovat na rám.",
      image: "/images/batinstal1.png",
      icon: <ArrowRight className="w-6 h-6 text-green-600" />
    },
    {
      id: 2,
      title: "Příprava na rámu",
      description: "Přiložte montážní lištu na spodní rámovou trubku vašeho kola. Otvory v liště by měly lícovat s předvrtanými závity pro košík na láhev.",
      image: "/images/batinstal2.png",
      icon: <Wrench className="w-6 h-6 text-green-600" />
    },
    {
      id: 3,
      title: "Upevnění držáku",
      description: "Pomocí přiložených šroubů pevně přišroubujte lištu k rámu. Ujistěte se, že je lišta dotažená a na rámu se nijak neviklá.",
      image: "/images/batinstal3.png",
      icon: <CheckCircle className="w-6 h-6 text-green-600" />
    },
    {
      id: 4,
      title: "Nasazení a uzamčení baterie",
      description: "Nyní vezměte samotnou baterii a nasuňte ji na připevněnou lištu směrem dolů, dokud neuslyšíte cvaknutí. Následně baterii uzamkněte klíčkem, aby se za jízdy neuvolnila.",
      image: "/images/batinstal4.png",
      icon: <Lock className="w-6 h-6 text-green-600" />
    },
    {
      id: 5,
      title: "Hotovo!",
      description: "Baterie je úspěšně nainstalována na rámu. Nyní je vše připraveno k propojení kabelů s motorem.",
      image: "/images/batinstal5.png",
      icon: <CheckCircle className="w-6 h-6 text-green-600" />
    }
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 md:py-16 text-left relative animate-in fade-in duration-300">
      <h1 className="text-3xl md:text-4xl font-bold text-[#2a3b4c] mb-6 flex items-center gap-3">
        <Bike className="w-10 h-10 text-green-600" /> Kompletní průvodce montáží
      </h1>
      <p className="text-lg text-slate-600 mb-10 pb-6 border-b border-slate-100 max-w-2xl">
        Zde najdete podrobný návod, jak si elektrosadu krok za krokem nainstalovat sami doma. Postupujte podle jednotlivých fází.
      </p>

      <div className="space-y-16">
        
        {/* --- FÁZE 1 & 2: PŘÍPRAVA A MOTOR --- */}
        <div className="space-y-12">
          
          {/* Krok 1 */}
          <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm gap-4">
            <h3 className="font-bold text-xl text-[#2a3b4c] mb-3 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-amber-500" /> Krok 1: Příprava kola (Demontáž starého středu)
            </h3>
            <p className="text-slate-600 mb-6 max-w-3xl">
              Než začneme s instalací nového motoru, je nutné sundat původní kliky a vylisovat starou středovou osu z rámu vašeho kola. V tomto krátkém videu uvidíte, jaké nářadí k tomu budete potřebovat a jak na to.
            </p>
            <VideoEmbed embedId="P4QA7gSwHJk" title="Demontáž středové osy" />
          </div>

          {/* Krok 2 - Motor s přepínáním videí */}
          <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm gap-6">
            <h3 className="font-bold text-xl text-[#2a3b4c] mb-3 flex items-center gap-2">
              <Video className="w-6 h-6 text-green-600" /> Krok 2: Montáž motoru (Výběr výkonu)
            </h3>
            <p className="text-slate-600 max-w-3xl mb-6">
              Vyberte si níže výkon motoru, který jste si u nás zakoupili. Videonávod vás provede kompletní montáží středového motoru do rámu, montáží klik i senzoru rychlosti.
            </p>
            
            {/* Přepínací tlačítka (Záložky) */}
            <div className="flex flex-wrap gap-3 mb-8">
              <button 
                onClick={() => setActiveMotorTab('250w')}
                className={`px-5 py-2.5 rounded-lg font-bold transition-all ${activeMotorTab === '250w' ? 'bg-green-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Motor 250W / 350W
              </button>
              <button 
                onClick={() => setActiveMotorTab('750w')}
                className={`px-5 py-2.5 rounded-lg font-bold transition-all ${activeMotorTab === '750w' ? 'bg-green-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Motor 750W (TSDZ8)
              </button>
              <button 
                onClick={() => setActiveMotorTab('1000w')}
                className={`px-5 py-2.5 rounded-lg font-bold transition-all ${activeMotorTab === '1000w' ? 'bg-green-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Motor 1000W (TSDZ16)
              </button>
            </div>

            {/* Videa samotná */}
            {activeMotorTab === '250w' && <VideoEmbed embedId="mo5gdXrD-QA" title="Montáž motoru 250W" />}
            {activeMotorTab === '750w' && <VideoEmbed embedId="ws4ie13Zpvg" title="Montáž motoru 750W" />}
            {activeMotorTab === '1000w' && <VideoEmbed embedId="lBFV6vTKxnA" title="Montáž motoru 1000W" />}
          </div>
        </div>

        {/* --- FÁZE 3: BATERIE --- */}
        <div className="pt-10 border-t border-slate-200">
          <h3 className="text-2xl font-bold text-[#2a3b4c] mb-8 flex items-center gap-3">
            Krok 3: Montáž baterie
          </h3>
          
          {/* Typ A: Rámová baterie */}
          <div className="mb-16">
            <h4 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-md text-sm uppercase tracking-wider">Typ A</span>
              Rámová baterie (na spodní trubku)
            </h4>
            <div className="space-y-10">
              {batterySteps.map((step, index) => (
                <div 
                  key={step.id} 
                  className={`flex flex-col md:flex-row gap-6 items-center bg-white p-6 rounded-2xl border border-slate-100 shadow-sm ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
                >
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 text-green-600 font-bold text-lg shrink-0">
                        {step.id}
                      </span>
                      <h4 className="text-xl font-bold text-[#2a3b4c]">
                        {step.title}
                      </h4>
                    </div>
                    <p className="text-slate-600 pl-14 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  <div className="flex-1 w-full relative">
                    <div className="bg-slate-50 p-2 rounded-xl border border-slate-200 shadow-inner">
                      <img 
                        src={step.image} 
                        alt={step.title} 
                        className="w-full h-auto rounded-lg object-contain max-h-[300px]"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Typ B: Nosičová baterie */}
          <div>
            <h4 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-md text-sm uppercase tracking-wider">Typ B</span>
              Nosičová (zadní) baterie
            </h4>
            
            <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
              <p className="text-slate-600 mb-6 max-w-3xl">
                Tato varianta je ideální pro majitele městských kol, nebo kol s nedostatkem místa v rámu. Podívejte se na náš detailní videonávod, jak namontovat zadní nosič a správně do něj uchytit baterii.
              </p>
              <VideoEmbed embedId="dnw-Q0kL5xI" title="Montáž nosičové baterie" />
            </div>
          </div>

        </div>

        {/* --- FÁZE 4: ZAPOJENÍ --- */}
        <div className="pt-12 border-t border-slate-200">
          <h3 className="text-2xl font-bold text-[#2a3b4c] mb-10 flex items-center gap-3">
            <Zap className="w-8 h-8 text-amber-500" />
            Krok 4: Zapojení elektrických kabelů
          </h3>
          
          <div className="flex flex-col md:flex-row-reverse gap-8 items-center bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
            
            <div className="flex-1 space-y-5">
              <p className="text-lg text-slate-600 leading-relaxed">
                Posledním krokem je propojení celého systému. Motor se zapojuje jednoduše podle přiloženého schématu.
              </p>
              <div className="bg-amber-50 p-6 rounded-xl border border-amber-100 space-y-3">
                <p className="font-bold text-amber-900 text-sm">✓ Důležitá informace:</p>
                <p className="text-sm text-amber-800">
                  Nemusíte se bát chyby. <strong className="font-bold">Každá koncovka má jiný tvar i barvu</strong>, takže konektory jednoduše nelze zapojit špatně. Vždy pasuje pouze protikus do protikusu.
                </p>
              </div>
              <p className="text-base text-slate-600 leading-relaxed italic">
                Pro lepší uchycení, bezpečí za jízdy a estetiku doporučujeme kabely připevnit k rámu kola pomocí elektrikářských pásek (stahovaček) nebo izolepy dle vlastního uvážení.
              </p>
            </div>

            {/* Klikací obrázek schématu */}
            <div className="flex-1 w-full relative shrink-0">
              <div 
                className="group relative bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-inner cursor-pointer hover:border-green-400 transition-colors"
                onClick={() => setIsSchemaOpen(true)}
              >
                <img 
                  src="/images/shcema.png" 
                  alt="Schéma zapojení e-bike motoru" 
                  className="w-full h-auto rounded-lg object-contain transition-transform group-hover:scale-[1.02]"
                />
                
                <div className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm text-slate-700 px-4 py-2 rounded-lg font-bold text-sm shadow-md flex items-center gap-2 transition-all group-hover:bg-green-600 group-hover:text-white">
                  <Maximize2 className="w-4 h-4" /> Zvětšit schéma
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* --- VYSKAKOVACÍ OKNO PRO ZVĚTŠENÍ SCHÉMATU --- */}
      {isSchemaOpen && (
        <div 
          className="fixed inset-0 z-[100] bg-slate-900/95 flex items-center justify-center p-4 backdrop-blur-sm cursor-zoom-out"
          onClick={() => setIsSchemaOpen(false)}
        >
          <div className="relative max-w-7xl max-h-screen flex flex-col items-center justify-center w-full h-full">
            
            <button 
              className="absolute top-4 right-4 md:top-8 md:right-8 z-50 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-colors cursor-pointer shadow-lg"
              onClick={(e) => {
                e.stopPropagation();
                setIsSchemaOpen(false);
              }}
              aria-label="Zavřít"
            >
              <X className="w-8 h-8" />
            </button>
            
            <img 
              src="/images/shcema.png" 
              alt="Schéma zapojení zvětšené" 
              className="w-full h-full object-contain drop-shadow-2xl cursor-default rounded-xl relative z-40"
              onClick={(e) => e.stopPropagation()} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidesPage;